import React, { useState, useRef, useEffect } from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import DatePicker from 'react-datepicker';


type ShipmentDropProps = {
    fetchShipments: ({
        statuses,
        dateOfJourneyFrom,
        dateOfJourneyTo,
        createdDateFrom,
        createdDateTo
    }: {
        statuses: string[];
        dateOfJourneyFrom?: Date;
        dateOfJourneyTo?: Date;
        createdDateFrom?: Date;
        createdDateTo?: Date;
    }) => void;
};

export function ShipmentDrop({ fetchShipments }: ShipmentDropProps) {
    const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Date filter states
    const [dateOfJourneyFrom, setDateOfJourneyFrom] = useState<Date | undefined>(undefined);
    const [dateOfJourneyTo, setDateOfJourneyTo] = useState<Date | undefined>(undefined);
    const [createdDateFrom, setCreatedDateFrom] = useState<Date | undefined>(undefined);
    const [createdDateTo, setCreatedDateTo] = useState<Date | undefined>(undefined);

    const options = [
        { value: 'Shipment Booked', label: 'Shipment Booked' },
        { value: 'Shipment Started', label: 'In Transit' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'Shipment Ended', label: 'Shipment Ended' },
        { value: 'Delivered', label: 'Delivered' },
        { value: 'Partially Delivered', label: 'Partially Delivered' },
    ];

    // Clear selected statuses and apply filters
    const handleClearStatus = () => {
        setSelectedStatus([]);
        setDateOfJourneyFrom(undefined);
        setDateOfJourneyTo(undefined);
        setCreatedDateFrom(undefined);
        setCreatedDateTo(undefined);
        fetchShipments({
            statuses: [],
            dateOfJourneyFrom: undefined,
            dateOfJourneyTo: undefined,
            createdDateFrom: undefined,
            createdDateTo: undefined,
        });
    };

    // Apply filters based on selected statuses and date ranges
    const handleApplyFilters = () => {
        fetchShipments({
            statuses: selectedStatus,
            dateOfJourneyFrom,
            dateOfJourneyTo,
            createdDateFrom,
            createdDateTo,
        });
    };

    // Toggle dropdown for status selection
    const handleStatusChange = (value: string) => {
        setSelectedStatus((prev) =>
            prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
        );
    };

    // Remove selected status
    const handleRemoveStatus = (value: string) => {
        setSelectedStatus((prev) => prev.filter((item) => item !== value));
    };

    // Toggle dropdown visibility
    const toggleDropdown = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent the event from propagating to the document
        setDropdownOpen((prev) => !prev);
    };

    // Close dropdown when clicking outside
    const closeDropdown = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setDropdownOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', closeDropdown);
        return () => {
            document.removeEventListener('click', closeDropdown);
        };
    }, []);

    return (
        <div className='menu menu-sub menu-sub-dropdown w-250px w-md-300px' data-kt-menu='true' ref={dropdownRef}>
            <div className='px-7 py-5'>
                <div className='fs-5 text-dark fw-bolder'>Filter Options</div>
            </div>

            <div className='separator border-gray-200'></div>

            <div className='px-7 py-5'>
                {/* Status Filter */}
                <div className="mb-3" onClick={(e) => e.stopPropagation()}>
                    <label className="form-label fw-bold">Status:</label>
                    <div className="custom-select-container position-relative" onClick={toggleDropdown}>
                        <div
                            className="border rounded py-2 px-3 d-flex flex-wrap align-items-center gap-2"
                            style={{ cursor: "pointer", backgroundColor: "#F9F9F9", minHeight: "45px", width: "250px" }}
                        >
                            {selectedStatus.length > 0 ? (
                                selectedStatus.map((status) => (
                                    <span
                                        key={status}
                                        className="badge bg-primary d-flex align-items-center gap-2"
                                        style={{ padding: "8px 12px", fontSize: "14px", borderRadius: "15px" }}
                                    >
                                        {options.find((option) => option.value === status)?.label}
                                        <FaTimes
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveStatus(status);
                                            }}
                                            style={{ cursor: "pointer", fontSize: "12px", marginLeft: "5px" }}
                                        />
                                    </span>
                                ))
                            ) : (
                                <span className="text-muted">Select option</span>
                            )}
                        </div>

                        {dropdownOpen && (
                            <ul
                                className="list-group position-absolute mt-1 w-100 shadow-sm"
                                style={{
                                    zIndex: "1",
                                    maxHeight: "180px",
                                    overflowY: "auto",
                                    width: "250px",
                                    backgroundColor: "#fff",
                                    borderRadius: "5px",
                                }}
                            >
                                {options.map((option) => (
                                    <li
                                        key={option.value}
                                        className={`list-group-item d-flex justify-content-between align-items-center border-0 ${selectedStatus.includes(option.value) ? "bg-light text-primary" : ""
                                            }`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleStatusChange(option.value);
                                        }}
                                        style={{ cursor: "pointer", padding: "10px 15px" }}
                                    >
                                        {option.label}
                                        {selectedStatus.includes(option.value) && <FaCheck />}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Date of Journey Filter */}
                <div className="mb-3">
                    <label className="form-label fw-bold">Date of Journey:</label>
                    <div className="d-flex align-items-center"  >
                        <div onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => e.stopPropagation()}>
                            <DatePicker
                                selected={dateOfJourneyFrom}
                                onChange={(date) => setDateOfJourneyFrom(date ? date : undefined)}
                                selectsStart
                                startDate={dateOfJourneyFrom}
                                endDate={dateOfJourneyTo}
                                // maxDate={new Date()} 
                                className="form-control"
                                placeholderText="From"
                                dateFormat="dd/MM/yyyy"
                            // Prevent closing dropdown on date picker click
                            />
                        </div>

                        <span className="mx-2">to</span>
                        <div onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => e.stopPropagation()}>
                            <DatePicker
                                selected={dateOfJourneyTo}
                                onChange={(date) => setDateOfJourneyTo(date ? date : undefined)}
                                selectsEnd
                                startDate={dateOfJourneyFrom}
                                endDate={dateOfJourneyTo}
                                minDate={dateOfJourneyFrom}
                                // maxDate={new Date()} 
                                className="form-control"
                                placeholderText="To"
                                dateFormat="dd/MM/yyyy"
                            // Prevent closing dropdown on date picker click
                            />
                        </div>

                    </div>
                </div>

                {/* Created Date Filter */}
                <div className="mb-3">
                    <label className="form-label fw-bold">Created Date:</label>
                    <div className="d-flex align-items-center">
                        <div onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => e.stopPropagation()}>
                            <DatePicker
                                selected={createdDateFrom}
                                onChange={(date) => setCreatedDateFrom(date ? date : undefined)}
                                selectsStart
                                startDate={createdDateFrom}
                                endDate={createdDateTo}
                                maxDate={new Date()} 
                                className="form-control"
                                placeholderText="From"
                                dateFormat="dd/MM/yyyy"
                            // Prevent closing dropdown on date picker click
                            />
                        </div>

                        <span className="mx-2">to</span>
                        <div onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => e.stopPropagation()}>
                            <DatePicker
                                selected={createdDateTo}
                                onChange={(date) => setCreatedDateTo(date ? date : undefined)}
                                selectsEnd
                                startDate={createdDateFrom}
                                endDate={createdDateTo}
                                minDate={createdDateFrom}
                                maxDate={new Date()} 
                                className="form-control"
                                placeholderText="To"
                                dateFormat="dd/MM/yyyy"
                            // Prevent closing dropdown on date picker click
                            />
                        </div>

                    </div>
                </div>

                <div className='d-flex justify-content-end'>
                    <button
                        type='reset'
                        className='btn btn-sm btn-white btn-active-light-primary me-2'
                        data-kt-menu-dismiss='true'
                        onClick={handleClearStatus}
                    >
                        Reset
                    </button>

                    <button
                        type='submit'
                        className='btn btn-sm btn-primary'
                        data-kt-menu-dismiss='true'
                        onClick={handleApplyFilters}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
}
